/**
 * Free Gift with Purchase Selector - OPTIMIZED
 */

class CartStateManager {
  constructor() {
    this.cart = null;
    this.lastFetch = 0;
    this.fetchPromise = null;
    this.CACHE_DURATION = 800;
    this.pendingOperations = new Set();
  }

  async getCart(forceRefresh = false) {
    const now = Date.now();
    
    if (this.pendingOperations.size > 0 && !forceRefresh) {
      await Promise.all([...this.pendingOperations]);
    }
    
    if (this.fetchPromise) {
      return this.fetchPromise;
    }
    
    if (!forceRefresh && this.cart && (now - this.lastFetch < this.CACHE_DURATION)) {
      return Promise.resolve(this.cart);
    }
    
    this.fetchPromise = fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        this.cart = cart;
        this.lastFetch = Date.now();
        this.fetchPromise = null;
        return cart;
      })
      .catch(err => {
        this.fetchPromise = null;
        throw err;
      });
    
    return this.fetchPromise;
  }

  invalidate() {
    this.cart = null;
    this.lastFetch = 0;
  }

  addOperation(promise) {
    this.pendingOperations.add(promise);
    promise.finally(() => this.pendingOperations.delete(promise));
    return promise;
  }

  getCurrentTier() {
    const tierElement = document.querySelector('[data-current-tier]');
    if (tierElement) {
      return tierElement.dataset.currentTier || '0';
    }
    
    if (!this.cart) return '0';
    
    const total = this.cart.total_price;
    const tier1El = document.querySelector('[data-tier-1-threshold]');
    const tier2El = document.querySelector('[data-tier-2-threshold]');
    const tier3El = document.querySelector('[data-tier-3-threshold]');
    
    const tier1 = tier1El ? parseInt(tier1El.dataset.tier1Threshold) : 10000;
    const tier2 = tier2El ? parseInt(tier2El.dataset.tier2Threshold) : 20000;
    const tier3 = tier3El ? parseInt(tier3El.dataset.tier3Threshold) : 30000;
    
    if (tier3 && total >= tier3) return '3';
    if (tier2 && total >= tier2) return '2';
    if (tier1 && total >= tier1) return '1';
    return '0';
  }
}

class GranularDOMUpdater {
  static async updateTotals() {
    try {
      const cart = await window.cartState.getCart(true);
      
      const subtotalEl = document.querySelector('.totals__total-value');
      if (subtotalEl) {
        const giftTotal = cart.items
          .filter(i => i.properties?._gwp_gift === 'true')
          .reduce((sum, i) => sum + i.final_line_price, 0);
        
        const subtotal = cart.total_price - giftTotal;
        
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          subtotalEl.textContent = Shopify.formatMoney(subtotal);
        } else {
          const dollars = (subtotal / 100).toFixed(2);
          subtotalEl.textContent = `$${dollars}`;
        }
      }
      
      const badge = document.querySelector('#cart-icon-bubble .cart-count-bubble span');
      if (badge) {
        badge.textContent = cart.item_count;
      }
      
    } catch (error) {
      console.error('[GWP] Totals update failed:', error);
    }
  }

  static async updateGiftSelector() {
    try {
      const timestamp = Date.now();
      const response = await fetch(`/?sections=cart-drawer&_=${timestamp}`);
      const sections = await response.json();
      
      if (!sections['cart-drawer']) {
        console.warn('[GWP] No cart-drawer section in response');
        return;
      }
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const newSelector = doc.querySelector('[data-gift-selector]');
      const currentSelector = document.querySelector('[data-gift-selector]');
      
      if (!newSelector) {
        if (currentSelector) {
          currentSelector.remove();
        }
        return;
      }
      
      if (!currentSelector) {
        const progressBar = document.querySelector('[data-gift-progress]');
        if (progressBar && progressBar.parentElement) {
          progressBar.parentElement.insertAdjacentHTML('afterend', newSelector.outerHTML);
          setTimeout(() => new FreeGiftSelector(), 30);
        }
        return;
      }
      
      if (currentSelector._selectorInstance) {
        currentSelector._selectorInstance.destroy();
      }
      
      currentSelector.outerHTML = newSelector.outerHTML;
      
      setTimeout(() => {
        const updatedSelector = document.querySelector('[data-gift-selector]');
        if (updatedSelector && !updatedSelector._selectorInstance) {
          new FreeGiftSelector();
        }
      }, 30);
      
    } catch (error) {
      console.error('[GWP] Selector update failed:', error);
    }
  }

  static async updateLineItems() {
    try {
      const timestamp = Date.now();
      const response = await fetch(`/?sections=cart-drawer&_=${timestamp}`);
      const sections = await response.json();
      
      if (!sections['cart-drawer']) return;
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const newItems = doc.querySelector('#CartDrawer-CartItems');
      const currentItems = document.querySelector('#CartDrawer-CartItems');
      
      if (newItems && currentItems) {
        currentItems.innerHTML = newItems.innerHTML;
      }
      
      await this.updateTotals();
      
    } catch (error) {
      console.error('[GWP] Line items update failed:', error);
    }
  }
}

class FreeGiftSelector {
  constructor() {
    this.selector = document.querySelector('[data-gift-selector]');
    if (!this.selector || this.selector._selectorInstance) return;
    
    this.selector._selectorInstance = this;
    this.isUpdating = false;
    
    if (!window._gwpAutoAddComplete) {
      window._gwpAutoAddComplete = {};
    }
    
    this.init();
  }

  init() {
    this.selector.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-add-gift-gwp]');
      if (addBtn) {
        e.preventDefault();
        this.handleAddGift(addBtn.closest('.gift-option'));
      }
    });

    setTimeout(() => this.checkAndAutoAddDefaultGift(), 50);
  }

  async checkAndAutoAddDefaultGift() {
    try {
      window.cartState.invalidate();
      const cart = await window.cartState.getCart(true);
      
      const currentTier = this.getCurrentTier();
      const allowMultiple = this.getCurrentTierAllowsMultiple();
      
      if (currentTier === '0') {
        return;
      }
      
      const tierKey = `tier_${currentTier}_${allowMultiple}`;
      if (window._gwpAutoAddComplete[tierKey]) {
        return;
      }
      
      const giftsInTier = cart.items.filter(item => 
        item.properties?._gwp_gift === 'true' &&
        item.properties?._free_gift_tier === currentTier
      );
      
      const totalGiftQuantity = giftsInTier.reduce((sum, item) => sum + item.quantity, 0);
      const expectedGiftCount = allowMultiple ? 2 : 1;
      
      if (totalGiftQuantity >= expectedGiftCount) {
        window._gwpAutoAddComplete[tierKey] = true;
        return;
      }
      
      const defaultGifts = this.selector.querySelectorAll('[data-is-default="true"]');
      
      if (defaultGifts.length === 0) {
        return;
      }
      
      window._gwpAutoAddComplete[tierKey] = 'in-progress';
      
      this.showLoadingOverlay();
      
      if (allowMultiple && defaultGifts.length > 1) {
        for (const gift of defaultGifts) {
          await this.addGiftToCart(
            gift.dataset.variantId,
            gift.dataset.productId,
            currentTier
          );
          await new Promise(r => setTimeout(r, 50));
        }
      } else {
        await this.addGiftToCart(
          defaultGifts[0].dataset.variantId,
          defaultGifts[0].dataset.productId,
          currentTier
        );
      }
      
      await new Promise(r => setTimeout(r, 200));
      
      window._gwpAutoAddComplete[tierKey] = true;
      
      window.cartState.invalidate();
      
      await Promise.all([
        GranularDOMUpdater.updateLineItems(),
        GranularDOMUpdater.updateGiftSelector()
      ]);
      
      this.hideLoadingOverlay();
      
    } catch (error) {
      console.error('[GWP] Auto-add failed:', error);
      const currentTier = this.getCurrentTier();
      const allowMultiple = this.getCurrentTierAllowsMultiple();
      const tierKey = `tier_${currentTier}_${allowMultiple}`;
      delete window._gwpAutoAddComplete[tierKey];
      this.hideLoadingOverlay();
    }
  }

  async handleAddGift(giftOption) {
    if (!giftOption || this.isUpdating) return;
    
    const variantId = giftOption.dataset.variantId;
    const productId = giftOption.dataset.productId;
    
    if (!variantId) {
      this.showErrorMessage('Variant not found');
      return;
    }
    
    this.isUpdating = true;
    this.showLoadingOverlay();
    
    try {
      const currentTier = this.getCurrentTier();
      const allowMultiple = this.getCurrentTierAllowsMultiple();
      
      await this.removeExistingFreeGifts();
      
      await new Promise(r => setTimeout(r, 150));
      
      window.cartState.invalidate();
      
      if (allowMultiple) {
        const allDefaults = this.selector.querySelectorAll('[data-is-default="true"]');
        for (const gift of allDefaults) {
          await this.addGiftToCart(
            gift.dataset.variantId,
            gift.dataset.productId,
            currentTier
          );
          await new Promise(r => setTimeout(r, 50));
        }
      } else {
        await this.addGiftToCart(variantId, productId, currentTier);
      }
      
      await new Promise(r => setTimeout(r, 200));
      
      const tierKey = `tier_${currentTier}_${allowMultiple}`;
      window._gwpAutoAddComplete[tierKey] = true;
      
      window.cartState.invalidate();
      await Promise.all([
        GranularDOMUpdater.updateLineItems(),
        GranularDOMUpdater.updateGiftSelector()
      ]);
      
      this.showSuccessMessage('Gift updated!');
      
    } catch (error) {
      console.error('[GWP] Add gift failed:', error);
      this.showErrorMessage('Failed to add gift');
    } finally {
      this.hideLoadingOverlay();
      this.isUpdating = false;
    }
  }

  async addGiftToCart(variantId, productId, tier) {
    console.log('=== ADD GIFT TO CART DEBUG ===');
    console.log('variantId:', variantId, 'type:', typeof variantId);
    console.log('productId:', productId, 'type:', typeof productId);
    console.log('tier:', tier);
    
    const cart = await window.cartState.getCart(true);
    console.log('Current cart items:', cart.items.length);
    
    const existingGift = cart.items.find(item => 
      item.variant_id.toString() === variantId.toString() &&
      item.properties?._gwp_gift === 'true'
    );
    
    if (existingGift) {
      console.log('Gift already exists in cart, skipping');
      return Promise.resolve(cart);
    }
    
    console.log('Preparing to add gift...');
    
    const requestBody = {
      items: [{
        id: variantId,
        quantity: 1,
        properties: {
          '_gwp_gift': 'true',
          '_free_gift_tier': tier,
          '_free_gift_product_id': productId
        }
      }]
    };
    
    console.log('Request body:', JSON.stringify(requestBody, null, 2));
    
    const operation = fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }).then(async response => {
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response as JSON');
        throw new Error('Invalid JSON response');
      }
      
      console.log('Parsed response data:', responseData);
      
      if (!response.ok) {
        console.error('Add to cart failed:', responseData);
        throw new Error(responseData.description || responseData.message || 'Failed to add gift');
      }
      return responseData;
    }).catch(error => {
      console.error('Error caught:', error);
      throw error;
    });
    
    window.cartState.addOperation(operation);
    return operation;
  }

  async removeExistingFreeGifts() {
    window.cartState.invalidate();
    const cart = await window.cartState.getCart(true);
    
    const currentTier = this.getCurrentTier();
    const allowMultiple = this.getCurrentTierAllowsMultiple();
    
    const giftsToRemove = cart.items.filter(item => {
      if (!item.properties?._gwp_gift) return false;
      
      const itemTier = item.properties._free_gift_tier || '1';
      if (itemTier !== currentTier) return true;
      if (allowMultiple) return false;
      return true;
    });
    
    if (giftsToRemove.length === 0) return;
    
    const updates = {};
    giftsToRemove.forEach(item => {
      updates[item.key] = 0;
    });
    
    const operation = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    }).then(r => r.json());
    
    window.cartState.addOperation(operation);
    await operation;
    
    await new Promise(r => setTimeout(r, 50));
  }

  getCurrentTier() {
    const container = this.selector.querySelector('[data-current-tier]');
    return container?.dataset.currentTier || '0';
  }

  getCurrentTierAllowsMultiple() {
    const container = this.selector.querySelector('.gift-tier-container');
    return container?.dataset.tierAllowMultiple === 'true';
  }

  destroy() {
    this.selector._selectorInstance = null;
  }

  showLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'gwp-loading-overlay visible';
    overlay.innerHTML = `
      <div class="gwp-loading-content">
        <div class="gwp-spinner"></div>
        <p class="gwp-loading-text">Updating Gift...</p>
      </div>
    `;
    
    if (this.selector?.parentElement) {
      this.selector.parentElement.insertBefore(overlay, this.selector.nextSibling);
    }
  }

  hideLoadingOverlay() {
    const overlay = document.querySelector('.gwp-loading-overlay');
    if (overlay) overlay.remove();
  }

  showSuccessMessage(msg) {
    this.showToast(msg, 'success');
  }

  showErrorMessage(msg) {
    this.showToast(msg, 'error');
  }

  showToast(msg, type) {
    const existing = document.querySelectorAll('.gift-toast');
    existing.forEach(t => t.remove());
    
    const toast = document.createElement('div');
    toast.className = `gift-toast gift-toast--${type}`;
    toast.textContent = msg;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#288326' : '#F26641'};
      color: white;
      padding: 12px 24px;
      border-radius: 6px;
      z-index: 9999;
      animation: slideIn 0.15s ease;
    `;
    
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
  }
}

async function checkTierEligibility() {
  if (checkTierEligibility.isRunning) return;
  checkTierEligibility.isRunning = true;
  
  try {
    window.cartState.invalidate();
    const cart = await window.cartState.getCart(true);
    const currentTier = window.cartState.getCurrentTier();
    
    const giftsToRemove = cart.items.filter(item => {
      if (!item.properties?._gwp_gift) return false;
      const giftTier = item.properties._free_gift_tier || '1';
      return currentTier === '0' || giftTier !== currentTier;
    });
    
    if (giftsToRemove.length > 0) {
      const updates = {};
      giftsToRemove.forEach(gift => updates[gift.key] = 0);
      
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      window._gwpAutoAddComplete = {};
      
      window.cartState.invalidate();
      
      await Promise.all([
        GranularDOMUpdater.updateLineItems(),
        GranularDOMUpdater.updateGiftSelector()
      ]);
    }
    
  } catch (error) {
    console.error('[GWP] Tier check failed:', error);
  } finally {
    checkTierEligibility.isRunning = false;
  }
}

async function enforceGiftQuantities() {
  try {
    const cart = await window.cartState.getCart(true);
    const updates = {};
    let needsUpdate = false;
    
    cart.items.forEach(item => {
      if (item.properties?._gwp_gift === 'true' && item.quantity > 1) {
        updates[item.key] = 1;
        needsUpdate = true;
      }
    });
    
    if (needsUpdate) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      window.cartState.invalidate();
      await GranularDOMUpdater.updateLineItems();
    }
  } catch (error) {
    console.error('[GWP] Quantity enforcement failed:', error);
  }
}

let gwpInitializing = false;

function initGWP() {
  if (gwpInitializing) {
    return;
  }
  
  gwpInitializing = true;
  
  if (!window.cartState) {
    window.cartState = new CartStateManager();
  }
  
  const selector = document.querySelector('[data-gift-selector]');
  if (selector && !selector._selectorInstance) {
    new FreeGiftSelector();
  }
  
  setTimeout(() => {
    gwpInitializing = false;
  }, 300);
}

document.addEventListener('DOMContentLoaded', initGWP);

let initTimeout;
document.addEventListener('cart:updated', () => {
  clearTimeout(initTimeout);
  window._gwpAutoAddComplete = {};
  initTimeout = setTimeout(initGWP, 100);
});

// NEW: Listen for quantity changes in cart items
document.addEventListener('DOMContentLoaded', () => {
  let mutationTimeout;
  
  const handleCartMutation = () => {
    clearTimeout(mutationTimeout);
    mutationTimeout = setTimeout(async () => {
      window.cartState.invalidate();
      
      const cart = await window.cartState.getCart(true);
      const currentTier = window.cartState.getCurrentTier();
      
      // Trigger tier check and gift selector update
      if (currentTier !== '0') {
        await checkTierEligibility();
        await GranularDOMUpdater.updateGiftSelector();
      }
    }, 300);
  };
  
  // Watch for changes in cart items container
  const cartItemsObserver = new MutationObserver(handleCartMutation);
  
  const observeCartItems = () => {
    const cartItems = document.querySelector('#CartDrawer-CartItems');
    if (cartItems) {
      cartItemsObserver.observe(cartItems, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-quantity', 'value']
      });
    }
  };
  
  // Start observing
  observeCartItems();
  
  // Re-attach observer if cart drawer is rebuilt
  const drawerObserver = new MutationObserver(() => {
    observeCartItems();
  });
  
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    drawerObserver.observe(drawer, {
      childList: true,
      subtree: true
    });
  }
});

document.addEventListener('DOMContentLoaded', () => {
  let lastTotal = 0;
  let lastTier = '0';
  let pollInterval = 2500;
  
  const checkCart = async () => {
    try {
      const drawer = document.querySelector('cart-drawer');
      
      if (!drawer?.classList.contains('active')) {
        pollInterval = 4000;
        return;
      }
      
      await enforceGiftQuantities();
      
      const cart = await window.cartState.getCart(true);
      const currentTier = window.cartState.getCurrentTier();
      
      const totalChanged = cart.total_price !== lastTotal;
      const tierChanged = currentTier !== lastTier;
      
      if (totalChanged || tierChanged) {
        lastTotal = cart.total_price;
        lastTier = currentTier;
        pollInterval = 2000;
        
        if (tierChanged) {
          window._gwpAutoAddComplete = {};
          
          await checkTierEligibility();
          
          await GranularDOMUpdater.updateGiftSelector();
        } else {
          setTimeout(checkTierEligibility, 150);
        }
      }
      
    } catch (error) {
      pollInterval = 4000;
    }
  };
  
  const startPolling = () => {
    setTimeout(() => {
      checkCart().then(startPolling);
    }, pollInterval);
  };
  
  startPolling();
});

const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .gwp-loading-overlay {
    position: relative;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.95);
    margin: 0 0 1rem 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid rgba(162, 243, 161, 0.6);
  }
  
  .gwp-loading-content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .gwp-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #288326;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  .gwp-loading-text {
    font-size: 14px;
    font-weight: 600;
    color: #288326;
    margin: 0;
    font-family: 'Poppins', sans-serif;
  }
`;
document.head.appendChild(style);